import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionTitle from '@/components/base/SectionTitle';
import FaqAccordion from '@/components/feature/FaqAccordion';
import ContactSection from '@/components/feature/ContactSection';
import { usePageSeo } from '@/hooks/usePageSeo';
import { faqItems } from '@/mocks/faq';
import { principles, siteInfo, stats, testimonials } from '@/mocks/site';

const aboutStructuredData = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们｜瓜田求证社',
    url: 'https://lebangyouxuan.cn/about',
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://lebangyouxuan.cn/',
    email: siteInfo.email,
    slogan: siteInfo.slogan,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  },
];

export default function About() {
  usePageSeo({
    title: '关于我们 | 瓜田求证社 · 网络爆料求证与辟谣的内容原则',
    description:
      '了解瓜田求证社的品牌定位、内容原则与求证方法。我们以公开信息为基础，用来源比对与时间线还原事件脉络，对不确定结论保持克制并公开修订。',
    keywords: '关于瓜田求证社,求证方法,辟谣原则,网络爆料分析,信息核实',
    path: '/about',
    structuredData: aboutStructuredData,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100/60">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: '关于我们' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            我们只做一件事：把信息弄清楚
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
            {siteInfo.intro}
          </p>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50 py-12 md:py-16">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <div className="h-64 w-full overflow-hidden rounded-2xl border border-background-200 sm:h-72 md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20a%20research%20desk%20with%20notes%20and%20threads%20connecting%20cards%20on%20warm%20cream%20background%2C%20muted%20amber%20and%20deep%20jade%20green%20geometric%20shapes%2C%20minimal%20flat%20vector%20style%2C%20no%20text&width=900&height=800&seq=gt-about-team&orientation=landscape"
                alt="以暖色几何图形表现资料整理与线索串联的插画"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionTitle
              eyebrow="Who We Are"
              title="我们的定位"
              desc="不追热点速度，只在意一件事是否被说清楚。"
            />
            <div className="mt-6 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              <p>
                瓜田求证社由一群内容创作者、编辑和资料整理爱好者共同维护。我们不生产爆料，也不参与立场之争，
                只做一件相对枯燥的事：把已经流传的说法拆开，逐条去找它的来源。
              </p>
              <p>
                我们不认为存在永远正确的结论。信息会更新，证据会增加，所以每条档案都标注更新时间，
                一旦出现新的关键材料，我们会回到页面修改，并保留说明。
              </p>
              <p>
                对我们来说，一次成功的求证不是"赢了辩论"，而是让一部分人从此多了一个核对信息的习惯。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-xl border border-background-200 bg-background-100/60 p-4">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                    <i className={`${item.icon} text-base`}></i>
                  </span>
                  <p className="mt-3 font-heading text-xl font-black text-foreground-950">{item.value}</p>
                  <p className="font-label text-xs text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100/50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <SectionTitle
            eyebrow="Content Principles"
            title="内容原则"
            desc="这三条写在我们每个人的工作清单最上面，也决定了一条内容能不能被发布。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {principles.map((item, index) => (
              <article
                key={item.title}
                className={`rounded-xl border p-6 ${
                  index === 1 ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-50'
                }`}
              >
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                    index === 1 ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-accent-700'
                  }`}
                >
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950 md:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
          <SectionTitle eyebrow="Reader Voices" title="用户口碑" desc="来自不同读者的反馈，也是我们继续做下去的原因。" />
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item, index) => (
              <figure
                key={item.name}
                className={`flex h-full flex-col rounded-xl border p-6 ${
                  index === 2 ? 'border-accent-300 bg-accent-50' : 'border-background-200 bg-background-100/60'
                }`}
              >
                <i className={`ri-double-quotes-l text-2xl ${index === 2 ? 'text-accent-500' : 'text-primary-400'}`}></i>
                <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-800">{item.quote}</blockquote>
                <figcaption className="mt-6 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground-950 font-heading text-sm font-bold text-background-50">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="flex flex-col">
                    <span className="font-heading text-sm font-semibold text-foreground-950">{item.name}</span>
                    <span className="font-label text-xs text-foreground-600">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full border-b border-background-200 bg-background-100/50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl px-4 md:px-6">
          <SectionTitle eyebrow="Common Questions" title="常见问题" align="center" />
          <div className="mt-10">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}